import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { BrokenImage as BrokenImageIcon } from '@material-ui/icons';

import { PRODUCT_IMAGE_PATH } from '../../../products/constants/products-app-paths.constants';
import { formatPrice } from '../../../shared/utils/format-price.util';
import { OrderItem } from '../../types/order-item.type';
import OrderItemTypeChip from '../Chips/OrderItemTypeChip.component';

const PHOTO_STYLE = { width: 40, height: 40, objectFit: 'cover' as const, borderRadius: 4 };
const PHOTO_FALLBACK_STYLE = { width: 40, height: 40, color: '#bdbdbd' };

type Props = {
  items: OrderItem[];
  onIncrease: (id: string) => void;
  onDecrease: (id: string) => void;
  onChangeQuantity: (id: string, value: string) => void;
};

const TABLE_HEADER_CELLS: { key: string; label: string; align: 'left' | 'center' | 'right'; minWidth: number }[] = [
  { key: 'photo', label: '', align: 'left', minWidth: 56 },
  { key: 'type', label: 'Type', align: 'left', minWidth: 150 },
  { key: 'name', label: 'Nom', align: 'left', minWidth: 250 },
  { key: 'category', label: 'Catégorie', align: 'left', minWidth: 250 },
  { key: 'subCategory', label: 'Sous-catégorie', align: 'left', minWidth: 250 },
  { key: 'price', label: 'Prix/u', align: 'left', minWidth: 150 },
  { key: 'quantity', label: 'Quantité', align: 'center', minWidth: 120 },
  { key: 'total', label: 'Total', align: 'right', minWidth: 150 },
];

export function ProductsTable({ items, onIncrease, onDecrease, onChangeQuantity }: Props) {
  const totalQuantity = items.reduce((acc, curr) => acc + curr.quantity, 0);
  const totalPrice = items.reduce((acc, curr) => acc + curr.price * curr.quantity, 0);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {TABLE_HEADER_CELLS.map((cell) => (
              <TableCell
                key={cell.key}
                align={cell.align}
                size="small"
                variant="head"
                style={{ minWidth: cell.minWidth }}
              >
                <b>{cell.label}</b>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={TABLE_HEADER_CELLS.length} align="center">
                Aucun produit
              </TableCell>
            </TableRow>
          )}
          {items.length > 0 &&
            items.map((item) => (
              <TableRow key={item.productId}>
                <TableCell style={{ width: 56 }}>
                  {item.photo ? (
                    <img src={PRODUCT_IMAGE_PATH(item.photo)} alt="" style={PHOTO_STYLE} />
                  ) : (
                    <BrokenImageIcon style={PHOTO_FALLBACK_STYLE} />
                  )}
                </TableCell>
                <TableCell>
                  <OrderItemTypeChip type={item.type} />
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.subcategory}</TableCell>
                <TableCell>{formatPrice(item.price)}</TableCell>
                <TableCell align="center">
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                    <Button
                      size="small"
                      style={{ minWidth: 0, padding: '0 0.5rem' }}
                      onClick={() => onDecrease(item.productId)}
                    >
                      -
                    </Button>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={item.quantity}
                      type="number"
                      inputProps={{ min: 1 }}
                      onChange={(event) => onChangeQuantity(item.productId, event.target.value)}
                      style={{ width: '5rem' }}
                    />
                    <Button
                      size="small"
                      style={{ minWidth: 0, padding: '0 0.5rem' }}
                      onClick={() => onIncrease(item.productId)}
                    >
                      +
                    </Button>
                  </div>
                </TableCell>
                <TableCell align="right">{formatPrice(item.price * item.quantity)}</TableCell>
              </TableRow>
            ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5} />
            <TableCell>
              <b>Total</b>
            </TableCell>
            <TableCell align="center">
              <b>{totalQuantity}</b>
            </TableCell>
            <TableCell align="right">
              <b>{formatPrice(totalPrice)}</b>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
